import { KeyOutlined, LogoutOutlined } from '@ant-design/icons';
import { history, KeepAliveContext, useIntl, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React, { useContext, useRef } from 'react';
import { flushSync } from 'react-dom';
import { logout } from '@/api/user/login/service';
import { clearToken, getToken } from '@/utils/token';
import HeaderDropdown from '../HeaderDropdown';
import ChangePwdModal, { type ChangePwdRef } from './ChangePwd';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  const changePwdRef = useRef<ChangePwdRef>(null);
  const keepAliveContext = useContext(KeepAliveContext);

  // 登出时清空多页签缓存，避免下个账号看到上个账号的页签
  const clearKeepAliveTabs = () => {
    const keepElements = (keepAliveContext as any)?.keepElements;
    if (!keepElements?.current) return;

    Object.keys(keepElements.current).forEach((key) => {
      delete keepElements.current[key];
    });
  };

  /**
   * 退出登录，直接回到登录页（不保留 redirect，登录后统一进首页）
   */
  const loginOut = () => {
    // 先捕获 token 并通知后端退出；请求拦截器异步执行时本地 token 可能已清空，
    // 必须显式传入，否则登出请求带 Bearer null 触发 401 弹窗
    const token = getToken();
    logout(token).catch((e: unknown) => {
      // 登出接口失败不影响本地退出流程，但保留痕迹便于排障
      console.warn(
        '【登出】后端退出接口调用失败',
        e instanceof Error ? e.message : e,
      );
    });

    // 清除本地存储的 token 和用户状态
    clearToken();
    clearKeepAliveTabs();
    flushSync(() => {
      setInitialState((s) => ({ ...s, currentUser: undefined }));
    });

    // 跳转到登录页
    if (window.location.pathname !== '/user/login') {
      history.replace({ pathname: '/user/login' });
    }
  };
  const { styles } = useStyles();
  const intl = useIntl();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      loginOut();
      return;
    }
    if (key === 'changePwd') {
      changePwdRef.current?.open();
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  const menuItems = [
    {
      key: 'changePwd',
      icon: <KeyOutlined />,
      label: intl.formatMessage({ id: 'globalHeader.avatar.changePwd' }),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: intl.formatMessage({ id: 'menu.account.logout' }),
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>
      <ChangePwdModal ref={changePwdRef} />
    </>
  );
};
